<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import CountryService from "@/services/CountryService";
import Swal from "sweetalert2";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCaretDown, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
library.add(faCaretDown, faLocationDot);

// IMPORTACION DE IMÁGENES DE PAÍSES
const countryImages = import.meta.glob('@/assets/img/*.png', { eager: true });
const getCountryImage = (code) => {
  const key = `/src/assets/img/${code.toLowerCase()}.png`;
  return countryImages[key]?.default || '';
};

const router = useRouter();
const authStore = useAuthStore();
const isLoading = ref(false);
const countryCode = ref("pa");
const showCountrySelector = ref(false);
const availableCountries = ref([]);

// Computar la imagen del país basado en el código
const countryImage = computed(() => {
  return getCountryImage(countryCode.value);
});

// Obtener el nombre del país actual
const currentCountryName = computed(() => {
  const country = availableCountries.value.find(
    (c) => c.code?.toLowerCase() === countryCode.value
  );
  return country ? country.name : "Panamá";
});

// Obtener beneficios del país actual
const currentCountryBenefits = computed(() => {
  const country = availableCountries.value.find(
    (c) => c.code?.toLowerCase() === countryCode.value
  );
  return (
    country?.general_benefits || [
      "Acceso especial a eventos y lanzamientos",
      "Participación en los mejores hike de la región",
      "Acumulación de puntos",
      "Descuentos exclusivos",
    ]
  );
});
const checkAuthentication = async () => {
  isLoading.value = true;
  try {
    const isAuthenticated = await authStore.checkAuth();
    if (!isAuthenticated) {
      // Mostrar toast de notificación
      Swal.fire({
        icon: "warning",
        title: "Sesión no iniciada",
        text: "Debes iniciar sesión para continuar",
        toast: true,
        position: "top-right",
        timer: 3000,
        showConfirmButton: false,
      });

      // Redirigir al login
      setTimeout(() => {
        router.push("/login");
      }, 1000);
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error verificando autenticación:", error);

    // Mostrar toast de error
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No pudimos verificar tu sesión",
      toast: true,
      position: "top-right",
      timer: 3000,
      showConfirmButton: false,
    });

    // Redirigir al login por seguridad
    setTimeout(() => {
      router.push("/login");
    }, 1000);
    return false;
  } finally {
    isLoading.value = false;
  }
};

// Cargar países disponibles
const loadCountries = async () => {
  isLoading.value = true;
  try {
    // Obtener solo países permitidos (PA, CR, GT)
    const countries = await CountryService.getAvailableCountries();

    // Asegurarse que todos tienen código
    availableCountries.value = countries.map((country) => {
      if (!country.code) {
        country.code = CountryService.getCountryCodeFromName(country.name);
      }
      return country;
    });

    // Si el usuario tiene un país seleccionado, usarlo
    const user = authStore.user;
    if (user?.country?.id) {
      const userCountry = availableCountries.value.find(
        (c) => c.id === user.country.id
      );
      if (userCountry) {
        countryCode.value = userCountry.code.toLowerCase();
      }
    } else {
      // Cargar del localStorage si existe
      const savedCountry = localStorage.getItem("selectedCountry");
      if (savedCountry) {
        countryCode.value = savedCountry;
      } else {
        // Intentar detectar país
        await detectCountry();
      }
    }
  } catch (error) {
    console.error("Error cargando países:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No pudimos cargar la información de países",
      toast: true,
      position: "top-right",
      timer: 3000,
      showConfirmButton: false,
    });
  } finally {
    isLoading.value = false;
  }
};

// Función para detectar el país
const detectCountry = async () => {
  try {
    const detectedCode = await CountryService.detectUserCountry();

    // Verificar si está entre los disponibles
    const validCodes = ["pa", "cr", "gt"];
    if (validCodes.includes(detectedCode)) {
      countryCode.value = detectedCode;

      // Actualizar en el backend si es posible
      const country = availableCountries.value.find(
        (c) => c.code.toLowerCase() === detectedCode
      );

      if (country) {
        await selectCountry(country);
      }
    } else {
      countryCode.value = "pa";
    }
  } catch (error) {
    console.error("Error detectando país:", error);
  }
};

// Cambiar país
const selectCountry = async (country) => {
  const code = country.code.toLowerCase();
  countryCode.value = code;
  showCountrySelector.value = false;

  // Guardar la selección en localStorage
  localStorage.setItem("selectedCountry", code);

  // Actualizar el país en el backend
  try {
    isLoading.value = true;
    const result = await CountryService.updateUserCountry(country.id);

    // Actualizar el store con la información actualizada
    if (result && result.user) {
      authStore.setUser(result.user);
    }

    Swal.fire({
      icon: "success",
      title: "¡País actualizado!",
      text: `Has seleccionado ${country.name}`,
      timer: 1500,
      toast: true,
      position: "top-right",
      showConfirmButton: false,
    });
  } catch (error) {
    console.error("Error actualizando país:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No pudimos actualizar tu país. Inténtalo más tarde.",
      timer: 1500,
      toast: true,
      position: "top-right",
      showConfirmButton: false,
    });
  } finally {
    isLoading.value = false;
  }
};

const activateMembership = async () => {
  if (!(await checkAuthentication())) return;

  isLoading.value = true;

  try {
    const currentCountry = availableCountries.value.find(
      (c) => c.code.toLowerCase() === countryCode.value
    );

    if (currentCountry) {
      await selectCountry(currentCountry);
    }

    Swal.fire({
      icon: "success",
      title: "¡Genial!",
      text: "Te llevaremos a activar tu membresía",
      timer: 1500,
      toast: true,
      position: "top-right",
      showConfirmButton: false,
    });

    setTimeout(() => {
      router.push("/activate-membership");
    }, 1500);
  } catch (error) {
    console.error("Error:", error);
    Swal.fire({
      icon: "error",
      title: "Ocurrió un error",
      toast: true,
      position: "top-right",
      text: "No pudimos procesar tu solicitud. Inténtalo más tarde.",
    });
  } finally {
    isLoading.value = false;
  }
};

const skipActivation = async () => {
  if (!(await checkAuthentication())) return;

  Swal.fire({
    icon: "info",
    title: "Registro completado",
    text: "Puedes activar tu membresía más tarde desde tu perfil",
    timer: 2000,
    toast: true,
    position: "top right",
    showConfirmButton: false,
  });

  router.push("/dashboard");
};

// Función para mostrar el selector de país
const toggleCountrySelector = () => {
  showCountrySelector.value = !showCountrySelector.value;
};

// Cargar datos al montar el componente
onMounted(async () => {
  const isAuthenticated = await checkAuthentication();

  if (isAuthenticated) {
    loadCountries();
  }
  loadCountries();
});
</script>

<template>
  <div
    class="min-h-screen flex flex-col bg-white bg-onboarding bg-cover bg-center"
  >
    <main class="flex-1 flex flex-col items-center justify-center px-4 py-12">
      <div class="w-full max-w-2xl text-center mb-12">
        <div
          class="max-w-7xl mx-auto px-4 py-4 flex lg:justify-around items-center flex-col lg:flex-row"
        >
          <div class="flex items-center gap-2">
            <span class="text-2xl font-bold">Estás en</span>
            <img
              :src="countryImage"
              :alt="countryCode"
              class="w-20 h-20 rounded-full object-cover"
            />
            <span class="text-2xl font-bold">{{ currentCountryName }}</span>
          </div>
          <button
            @click="toggleCountrySelector"
            class="px-4 py-2 bg-[#99b642] text-white rounded-full text-xl font-medium hover:bg-[#8ba73b] transition-colors"
          >
            Cambiar país
            <font-awesome-icon :icon="['fas', 'caret-down']" />
          </button>
        </div>

        <!-- Selector de país -->
        <div
          v-if="showCountrySelector"
          class="country-selector bg-white rounded-lg shadow-lg p-4 mb-6"
        >
          <h3 class="text-lg font-semibold mb-4 text-gray-900">
            Selecciona tu país
          </h3>
          <div class="grid grid-cols-3 gap-4">
            <div
              v-for="country in availableCountries"
              :key="country.id"
              @click="selectCountry(country)"
              class="country-option cursor-pointer p-3 border rounded-lg hover:bg-gray-100 transition-colors flex flex-col items-center"
              :class="{
                'border-[#99b642] bg-[#f5f9e8]':
                  countryCode === country.code.toLowerCase(),
              }"
            >
              <img
                :src="getCountryImage(country.code)"
                :alt="country.name"
                class="w-4 h-4 lg:w-10 lg:h-10 rounded-full object-cover mb-2"
              />
              <span class="text-gray-800">{{ country.name }}</span>
            </div>
          </div>
        </div>

        <h1 class="text-4xl font-bold text-gray-900 my-12">
          Activa tu Membresía
        </h1>
        <div class="flex flex-col items-center gap-4 mb-6">
          <button
            @click="activateMembership"
            :disabled="isLoading"
            class="px-8 py-3 bg-[#99b642] text-white rounded-full font-medium hover:bg-[#8ba73b] transition-colors w-full md:w-auto text-center min-w-[300px]"
          >
            ¿Ya tienes tu membresía? Llegó el momento de activarla
          </button>
        </div>

        <div class="grid md:grid-cols-2 gap-8 text-left">
          <div>
            <h2 class="text-2xl font-semibold mb-4">Algunos beneficios</h2>
            <ul class="space-y-2 text-lg">
              <li
                v-for="(benefit, index) in currentCountryBenefits"
                :key="index"
              >
                • {{ benefit }}
              </li>
            </ul>
          </div>
          <div>
            <h2 class="text-2xl font-semibold mb-4">
              ¿Aún no tienes tu membresía?
            </h2>
            <p class="text-lg">
              Solicita tu membresía física sin costo en nuestras tiendas
              autorizadas
            </p>
            <br />
            <p v-if="countryCode === 'cr'">
              <span class="text-2xl">
                <!-- market alt -->
                <font-awesome-icon :icon="['fas', 'location-dot']" /> Multiplaza
                Escazú
              </span>
              <br />
              <span class="text-2xl">
                <font-awesome-icon :icon="['fas', 'location-dot']" /> Lincoln
                Plaza
              </span>
            </p>
            <p v-else-if="countryCode === 'gt'"></p>
            <p v-else>
              <span class="text-2xl">
                <font-awesome-icon :icon="['fas', 'location-dot']" /> Multiplaza
                Pacific
              </span>
              <br />
              <span class="text-2xl">
                <font-awesome-icon :icon="['fas', 'location-dot']" /> Albrook
                Mall
              </span>
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col items-center gap-4">
        <button
          @click="skipActivation"
          class="px-8 py-3 text-gray-500 hover:text-gray-700 transition-colors"
        >
          Saltar paso
        </button>
      </div>
    </main>
  </div>
</template>

<style scoped>
.country-selector {
  position: relative;
  z-index: 10;
}

.country-option {
  transition: all 0.3s ease;
}

.country-option:hover {
  transform: translateY(-2px);
}
.bg-onboarding {
  background-image: url("@/assets/img/bg-white-2.jpg");
  background-size: cover;
  background-position: center;
}
</style>
